import { Button, cn } from "@devhop/ui";
import {
  ArrowRightIcon,
  BanknoteIcon,
  ChevronDownIcon,
  DownloadIcon,
  FileTextIcon,
  PrinterIcon,
} from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import type {
  AssignmentDTO,
  EventDTO,
  EventDetailReportDTO,
  EventFeaturesDTO,
  EventRehearsalSummaryDTO,
  EventShowDTO,
  RehearsalSessionDTO,
} from "../../domain/contracts";
import { eventHasFeature } from "../../domain/services/event-features";
import type {
  EventDetailSectionId,
  EventDetailTabId,
} from "../lib/event-detail-ui";
import type { ProfitDisplayMode, ProfitLossView } from "../lib/event-profit-loss-ui";
import { defaultProfitDisplayMode } from "../lib/event-profit-loss-ui";
import {
  downloadEventCostBreakdownCsv,
  downloadEventFinancialSnapshotCsv,
  downloadEventRosterCsv,
} from "../lib/event-report-csv";
import { performanceApi } from "../api/client";
import { EventDocumentTimelineSection } from "./EventDocumentTimelineSection";
import { EventFxRatesCard } from "./EventFxRatesCard";
import { EventPerformerCostReport } from "./EventPerformerCostReport";
import { EventProfitLossBreakdown } from "./EventProfitLossBreakdown";
import { EventProfitLossHero } from "./EventProfitLossHero";
import { EventRehearsalReportSection } from "./EventRehearsalReportSection";
import { PartialShowParticipationReport } from "./ShowAssignmentMatrix";

type PartialShowRow = {
  performerId: string;
  performerName: string;
  selectedCount: number;
  totalShowCount: number;
  eventShowIds: string[];
};

type EventReportsTabProps = {
  eventId: string;
  event: EventDTO;
  eventFeatures: EventFeaturesDTO;
  assignments: AssignmentDTO[];
  eventShows: EventShowDTO[];
  partialShowRows: PartialShowRow[];
  rehearsalSummary: EventRehearsalSummaryDTO | undefined;
  rehearsalSessions: RehearsalSessionDTO[] | undefined;
  isLoadingRehearsal: boolean;
  report: EventDetailReportDTO | undefined;
  isLoadingReport: boolean;
  canReadAssignments: boolean;
  canReadDocuments: boolean;
  canReadPayouts: boolean;
  canUpdateEvent: boolean;
  onTabClick: (tab: EventDetailTabId) => void;
  onSectionClick: (section: EventDetailSectionId) => void;
  onOpenRehearsals: () => void;
};

export function EventReportsTab({
  eventId,
  event,
  eventFeatures,
  assignments,
  eventShows,
  partialShowRows,
  rehearsalSummary,
  rehearsalSessions,
  isLoadingRehearsal,
  report,
  isLoadingReport,
  canReadAssignments,
  canReadDocuments,
  canReadPayouts,
  canUpdateEvent,
  onTabClick,
  onOpenRehearsals,
}: EventReportsTabProps) {
  const hasPerformers = eventHasFeature(eventFeatures, "performers");
  const hasStaff = eventHasFeature(eventFeatures, "staff");
  const staffPrimary = hasStaff && !hasPerformers;
  const hasRehearsals = eventHasFeature(eventFeatures, "rehearsals");
  const hasDocuments = eventHasFeature(eventFeatures, "documents");
  const hasMultiShow = eventShows.length >= 2;

  const [profitView, setProfitView] = useState<ProfitLossView>("realized");
  const [displayMode, setDisplayMode] = useState<ProfitDisplayMode>("native");
  const [exportOpen, setExportOpen] = useState(false);
  const [teamDetailsOpen, setTeamDetailsOpen] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);

  const printUrl = performanceApi.events.showRosterPrintUrl(eventId);
  const totalEventShowCount = Math.max(1, eventShows.length);

  const canShowProfit =
    !!report && (canReadDocuments || canReadPayouts || canReadAssignments);
  const canExportRoster =
    hasPerformers && canReadAssignments && assignments.length > 0;
  const canExportCost =
    hasPerformers && canReadAssignments && assignments.length > 0;
  const canExportFinancial =
    !!report && (canReadDocuments || canReadPayouts);
  const canExportAny = canExportRoster || canExportCost || canExportFinancial;

  const showTeamDetails =
    hasPerformers &&
    canReadAssignments &&
    (assignments.length > 0 || hasMultiShow || hasRehearsals);

  useEffect(() => {
    if (!report) return;
    setDisplayMode(defaultProfitDisplayMode(report.profitLoss, profitView));
  }, [profitView]);

  useEffect(() => {
    if (!report) return;
    setDisplayMode(defaultProfitDisplayMode(report.profitLoss, profitView));
  }, [
    report?.profitLoss.fxRates?.fxUsdToLak,
    report?.profitLoss.fxRates?.fxThbToLak,
  ]);

  const showFxCard =
    !!report?.profitLoss.fxRates &&
    (report.profitLoss.hasForeignRevenue ||
      report.profitLoss.fxRates.fxUsdToLak != null ||
      report.profitLoss.fxRates.fxThbToLak != null);

  const needsUsdRate =
    !!report &&
    (report.collected.USD !== 0 ||
      report.invoiced.USD !== 0 ||
      report.quoted.USD !== 0);
  const needsThbRate =
    !!report &&
    (report.collected.THB !== 0 ||
      report.invoiced.THB !== 0 ||
      report.quoted.THB !== 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-semibold text-lg tracking-tight">
            ກຳໄລ / ຂາດທຶນງານນີ້
          </h2>
          <p className="mt-1 text-muted-foreground text-sm">
            ເບິ່ງວ່າງານນີ້ກຳໄລ ຫຼື ຂາດທຶນເທົ່າໃດ — ສະຫຼັບເງິນຈິງ vs ຄາດການ
          </p>
        </div>
        {canExportAny ? (
          <div className="relative">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setExportOpen((open) => !open)}
            >
              <DownloadIcon className="size-4" />
              CSV
              <ChevronDownIcon className="size-4" />
            </Button>
            {exportOpen ? (
              <>
                <button
                  type="button"
                  className="fixed inset-0 z-10 cursor-default"
                  aria-label="ປິດເມນູ export"
                  onClick={() => setExportOpen(false)}
                />
                <div className="absolute top-full right-0 z-20 mt-1 min-w-52 rounded-lg border bg-popover p-1 shadow-md">
                  {canExportFinancial ? (
                    <ExportMenuItem
                      label="Financial / P&L CSV"
                      disabled={isLoadingReport || !report}
                      onClick={() => {
                        if (!report) return;
                        downloadEventFinancialSnapshotCsv({
                          eventId,
                          eventTitle: event.title,
                          report,
                        });
                        setExportOpen(false);
                      }}
                    />
                  ) : null}
                  {canExportCost ? (
                    <ExportMenuItem
                      label="Cost breakdown CSV"
                      onClick={() => {
                        downloadEventCostBreakdownCsv({
                          eventId,
                          eventTitle: event.title,
                          assignments,
                          totalEventShowCount,
                        });
                        setExportOpen(false);
                      }}
                    />
                  ) : null}
                  {canExportRoster ? (
                    <ExportMenuItem
                      label="Roster CSV"
                      onClick={() => {
                        downloadEventRosterCsv({
                          eventId,
                          eventTitle: event.title,
                          assignments,
                          eventShows,
                        });
                        setExportOpen(false);
                      }}
                    />
                  ) : null}
                </div>
              </>
            ) : null}
          </div>
        ) : null}
      </div>

      {isLoadingReport ? (
        <section className="rounded-2xl border bg-card px-6 py-16 text-center">
          <p className="text-muted-foreground text-sm">ກຳລັງຄິດກຳໄລ/ຂາດທຶນ...</p>
        </section>
      ) : !canShowProfit ? (
        <section className="rounded-2xl border bg-card px-6 py-12 text-center">
          <p className="font-medium text-sm">ບໍ່ມີສິດເບິ່ງກຳໄລ/ຂາດທຶນ</p>
          <p className="mt-2 text-muted-foreground text-sm">
            ຕ້ອງມີສິດເບິ່ງເອກະສານ ຫຼື ເບີກຈ່າຍ ຫຼື ທີມ
          </p>
        </section>
      ) : report ? (
        <>
          <EventProfitLossHero
            profitLoss={report.profitLoss}
            collected={report.collected}
            invoiced={report.invoiced}
            view={profitView}
            displayMode={displayMode}
            onViewChange={setProfitView}
            onDisplayModeChange={setDisplayMode}
            eventFeatures={eventFeatures}
            canReadDocuments={canReadDocuments}
          />
          {showFxCard && report.profitLoss.fxRates ? (
            <EventFxRatesCard
              eventId={eventId}
              fxRates={report.profitLoss.fxRates}
              canEdit={canUpdateEvent}
              needsUsdRate={needsUsdRate}
              needsThbRate={needsThbRate}
            />
          ) : null}
          <EventProfitLossBreakdown
            profitLoss={report.profitLoss}
            costBreakdown={report.profitLoss.costBreakdown}
            view={profitView}
            displayMode={displayMode}
            eventFeatures={eventFeatures}
            canReadAssignments={canReadAssignments}
            canReadPayouts={canReadPayouts}
            outstandingArLak={report.outstandingAr.LAK}
          />
        </>
      ) : null}

      <section className="flex flex-wrap gap-2">
        {hasDocuments && canReadDocuments ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => onTabClick("pricing")}
          >
            <FileTextIcon className="size-4" />
            ໄປລາຄາ / ເກັບເງິນ
          </Button>
        ) : null}
        {(hasPerformers || staffPrimary) && canReadPayouts ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => onTabClick("payout")}
          >
            <BanknoteIcon className="size-4" />
            ໄປເບີກຈ່າຍ
          </Button>
        ) : null}
        {hasPerformers && canReadAssignments ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => onTabClick("team")}
          >
            ໄປຈັດທີມ
            <ArrowRightIcon className="size-4" />
          </Button>
        ) : null}
      </section>

      {canReadDocuments && report && report.documents.length > 0 ? (
        <CollapsibleSection
          title="ເອກະສານ"
          subtitle={`${report.documents.length} ລາຍການ`}
          open={docsOpen}
          onToggle={() => setDocsOpen((value) => !value)}
        >
          <EventDocumentTimelineSection documents={report.documents} embedded />
        </CollapsibleSection>
      ) : null}

      {showTeamDetails ? (
        <CollapsibleSection
          title="ລາຍລະອຽດທີມ & ต้นทุน"
          subtitle="roster · partial show · ຄ່າຈ້າງ"
          open={teamDetailsOpen}
          onToggle={() => setTeamDetailsOpen((value) => !value)}
        >
          <div className="space-y-6">
            {hasMultiShow ? (
              <section className="space-y-3 rounded-xl border bg-card p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-sm">ນັກສະແດງ × ໂຊ</p>
                    <p className="mt-1 text-muted-foreground text-xs">
                      partial participation
                    </p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      window.open(printUrl, "_blank", "noopener,noreferrer")
                    }
                  >
                    <PrinterIcon className="size-4" />
                    ພິມ roster
                  </Button>
                </div>
                {partialShowRows.length > 0 ? (
                  <PartialShowParticipationReport
                    rows={partialShowRows}
                    eventShows={eventShows}
                  />
                ) : (
                  <p className="text-muted-foreground text-sm">
                    ທຸກຄົນເຂົ້າຮ່ວມຄົບທຸກໂຊ
                  </p>
                )}
              </section>
            ) : null}

            {hasRehearsals ? (
              <EventRehearsalReportSection
                hasTeam={assignments.length > 0}
                summary={rehearsalSummary}
                sessions={rehearsalSessions}
                isLoading={isLoadingRehearsal}
                onOpenSchedule={onOpenRehearsals}
              />
            ) : null}

            {assignments.length > 0 ? (
              <EventPerformerCostReport
                assignments={assignments}
                eventShows={eventShows}
                totalEventShowCount={totalEventShowCount}
              />
            ) : null}
          </div>
        </CollapsibleSection>
      ) : null}
    </div>
  );
}

function CollapsibleSection({
  title,
  subtitle,
  open,
  onToggle,
  children,
}: {
  title: string;
  subtitle: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-xl border bg-card">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left hover:bg-muted/30"
        onClick={onToggle}
      >
        <div>
          <p className="font-semibold text-sm">{title}</p>
          <p className="mt-0.5 text-muted-foreground text-xs">{subtitle}</p>
        </div>
        <ChevronDownIcon
          className={cn(
            "size-5 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-180",
          )}
        />
      </button>
      {open ? <div className="border-t px-5 py-4">{children}</div> : null}
    </section>
  );
}

function ExportMenuItem({
  label,
  disabled,
  onClick,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      className={cn(
        "flex w-full rounded-md px-3 py-2 text-left text-sm transition-colors",
        disabled
          ? "cursor-not-allowed text-muted-foreground"
          : "hover:bg-muted",
      )}
      onClick={onClick}
    >
      {label}
    </button>
  );
}
